import { lazy, Suspense, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from './lib/auth';
import { Loading } from './ui';
import StaffLayout from './layouts/StaffLayout';
import PortalLayout from './layouts/PortalLayout';
import { t } from './lib/i18n';

const Login = lazy(() => import('./pages/auth/Login'));
const Forgot = lazy(() => import('./pages/auth/Forgot'));
const ResetPassword = lazy(() => import('./pages/auth/ResetPassword'));
const Signup = lazy(() => import('./pages/auth/Signup'));
const Setup = lazy(() => import('./pages/auth/Setup'));
const Account = lazy(() => import('./pages/auth/Account'));
const MfaRequiredPage = lazy(() => import('./pages/auth/MfaSetup').then((m) => ({ default: m.MfaRequiredPage })));

const HomePage = lazy(() => import('./pages/home/Dashboard'));
const Tasks = lazy(() => import('./pages/home/Tasks'));
const People = lazy(() => import('./pages/people/PeopleRoutes'));
const Services = lazy(() => import('./pages/services/ServicesRoutes'));
const Checkins = lazy(() => import('./pages/checkins/CheckinsRoutes'));
const Kiosk = lazy(() => import('./pages/checkins/Kiosk'));
const PlanPrint = lazy(() => import('./pages/services/PlanPrint'));
const PassPrint = lazy(() => import('./pages/checkins/PassPrint'));
const Giving = lazy(() => import('./pages/giving/GivingRoutes'));
const Groups = lazy(() => import('./pages/groups/GroupsRoutes'));
const Calendar = lazy(() => import('./pages/calendar/CalendarRoutes'));
const Registrations = lazy(() => import('./pages/registrations/RegistrationsRoutes'));
const Chat = lazy(() => import('./pages/chat/Chat'));
const Publishing = lazy(() => import('./pages/publishing/Publishing'));
const Admin = lazy(() => import('./pages/admin/AdminRoutes'));
const Portal = lazy(() => import('./pages/portal/PortalRoutes'));
const PublicRoutes = lazy(() => import('./pages/public/PublicRoutes'));

function RequireAuth({ children, staff }: { children: ReactNode; staff?: boolean }) {
  const { me, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <Loading />;
  if (!me) return <Navigate to={`/entrar?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  // El personal sin verificación en dos pasos (obligatoria) la activa antes de entrar.
  if (staff && me.account.mfaSetupRequired) return <MfaRequiredPage />;
  if (staff && !me.isStaff) {
    // Enlaces de correos (p. ej. a un grupo) llevan al equivalente del portal.
    const group = /^\/groups\/([0-9a-f-]{36})/.exec(loc.pathname);
    const target = group ? `/portal/grupos/${group[1]}` : loc.pathname.startsWith('/services/plans/') ? '/portal/programacion' : loc.pathname === '/chat' ? `/portal/mensajes${loc.search}` : loc.pathname === '/cuenta' ? '/portal/cuenta' : '/portal';
    return <Navigate to={target} replace />;
  }
  return <>{children}</>;
}

export default function App() {
  return (
    <Suspense fallback={<Loading />}>
      <Routes>
        <Route path="/entrar" element={<Login />} />
        <Route path="/olvide" element={<Forgot />} />
        <Route path="/restablecer" element={<ResetPassword mode="reset" />} />
        <Route path="/activar" element={<ResetPassword mode="claim" />} />
        <Route path="/registro" element={<Signup />} />
        <Route path="/configuracion-inicial" element={<Setup />} />
        <Route path="/kiosko/*" element={<Kiosk />} />
        <Route path="/publico/*" element={<PublicRoutes />} />

        <Route path="/portal/*" element={<RequireAuth><PortalLayout><Portal /></PortalLayout></RequireAuth>} />
        {/* Páginas para imprimir: sin el menú lateral. */}
        <Route path="/services/plans/:id/imprimir" element={<RequireAuth staff><PlanPrint /></RequireAuth>} />
        <Route path="/checkins/tarjetas/imprimir" element={<RequireAuth staff><PassPrint /></RequireAuth>} />

        <Route element={<RequireAuth staff><StaffLayout /></RequireAuth>}>
          <Route index element={<HomePage />} />
          <Route path="tareas" element={<Tasks />} />
          <Route path="cuenta" element={<Account />} />
          <Route path="people/*" element={<People />} />
          <Route path="services/*" element={<Services />} />
          <Route path="checkins/*" element={<Checkins />} />
          <Route path="giving/*" element={<Giving />} />
          <Route path="groups/*" element={<Groups />} />
          <Route path="calendar/*" element={<Calendar />} />
          <Route path="registrations/*" element={<Registrations />} />
          <Route path="chat" element={<Chat />} />
          <Route path="publishing" element={<Publishing />} />
          <Route path="admin/*" element={<Admin />} />
        </Route>
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

function NotFound() {
  return (
    <div className="center-page">
      <div className="stack" style={{ textAlign: 'center' }}>
        <h1>{t('Página no encontrada')}</h1>
        <p className="muted">{t('La dirección no existe o fue movida.')}</p>
        <a href="/">{t('Volver al inicio')}</a>
      </div>
    </div>
  );
}
